/**
 * PersistentUI
 *
 * In-game HUD: retour, inventaire, musique, boucliers.
 * Menu and profile remain available via Escape / P.
 */

import { useEffect, useRef, useState, type RefObject } from 'react';
import { Item, Skill, Chapter, Achievement, ProfileTheme, CharacterPortrait } from '../types';
import type { InventoryUnlockNotice } from '../systems/inventory-manager';
import { ItemCard } from './item-card';
import { SkillCard } from './skill-card';
import { ProfilePanelCyberpunk } from './profile-panel-cyberpunk';
import { ShieldHud } from './shield-hud';
import { isMotionReduced } from '../systems/accessibility-settings';
import { useAccessibility } from '../systems/accessibility-context';
import styles from './persistent-ui.module.css';

const DRAWER_RECIBIR_MS = 260;
const DRAWER_OPEN_DELAY_MS = 620;
const FLY_DELAY_MS = 900;
const FLY_MS = 520;
const FLY_SAFETY_MS = FLY_MS + 400;
const FLY_EASING = 'cubic-bezier(.35,.05,.25,1)';
const HOLD_AFTER_ARRIVE_MS = 1200;
const HIDE_AFTER_ARRIVE_MS = 1560;
const REDUCED_CLOSE_MS = 2200;
const REDUCED_CENTER_MS = 1400;

interface InventoryUnlockFxState {
  visible: boolean;
  centerOn: boolean;
  drawerOpen: boolean;
  drawerAttend: boolean;
  drawerLabel: 'REÇOIT' | 'RANGÉ';
}

function useInventoryUnlockD2(
  notice: InventoryUnlockNotice | null,
  imageUrl?: string,
  replayKey = 0,
  paused = false,
  hudCadreRef?: RefObject<HTMLDivElement>,
  tiroirImgRef?: RefObject<HTMLImageElement>,
  centerImgRef?: RefObject<HTMLImageElement>,
  onUnreadReceived?: () => void,
  onEnd?: () => void,
): InventoryUnlockFxState {
  const [state, setState] = useState<InventoryUnlockFxState>({
    visible: false,
    centerOn: false,
    drawerOpen: false,
    drawerAttend: false,
    drawerLabel: 'REÇOIT',
  });
  const endedRef = useRef(false);
  const onUnreadRef = useRef(onUnreadReceived);
  const onEndRef = useRef(onEnd);
  onUnreadRef.current = onUnreadReceived;
  onEndRef.current = onEnd;

  useEffect(() => {
    if (!notice) {
      setState({
        visible: false,
        centerOn: false,
        drawerOpen: false,
        drawerAttend: false,
        drawerLabel: 'REÇOIT',
      });
      return;
    }

    if (paused) {
      setState({
        visible: false,
        centerOn: false,
        drawerOpen: false,
        drawerAttend: false,
        drawerLabel: 'REÇOIT',
      });
      return () => {};
    }

    endedRef.current = false;
    const timers: number[] = [];
    let flyEl: HTMLImageElement | null = null;
    let flyAnim: Animation | null = null;
    let arrived = false;

    const schedule = (fn: () => void, ms: number) => {
      timers.push(window.setTimeout(fn, ms));
    };

    const finish = () => {
      if (endedRef.current) return;
      endedRef.current = true;
      onEndRef.current?.();
      setState({
        visible: false,
        centerOn: false,
        drawerOpen: false,
        drawerAttend: false,
        drawerLabel: 'REÇOIT',
      });
    };

    const dropFly = () => {
      flyAnim?.cancel();
      flyAnim = null;
      flyEl?.remove();
      flyEl = null;
    };

    const markArrived = () => {
      if (arrived) return;
      arrived = true;
      dropFly();
      setState((prev) => ({
        ...prev,
        centerOn: false,
        drawerAttend: false,
        drawerLabel: 'RANGÉ',
      }));
      schedule(() => {
        setState((prev) => ({ ...prev, drawerOpen: false }));
        onUnreadRef.current?.();
      }, HOLD_AFTER_ARRIVE_MS);
      schedule(finish, HIDE_AFTER_ARRIVE_MS);
    };

    const startFly = () => {
      const cadre = hudCadreRef?.current;
      const source = centerImgRef?.current;
      const target = tiroirImgRef?.current;

      const flyImageUrl = imageUrl;
      if (
        isMotionReduced()
        || document.visibilityState === 'hidden'
        || !flyImageUrl
        || !cadre
        || !source
        || !target
      ) {
        markArrived();
        return;
      }

      const safety = window.setTimeout(markArrived, FLY_SAFETY_MS);

      const rs = cadre.getBoundingClientRect();
      const re = source.getBoundingClientRect();
      const rc = target.getBoundingClientRect();
      const vol = source.cloneNode(true) as HTMLImageElement;
      vol.className = styles.objVol ?? '';
      vol.src = flyImageUrl!;
      vol.alt = '';
      vol.style.width = `${re.width}px`;
      vol.style.height = `${re.height}px`;
      vol.style.left = `${re.left - rs.left}px`;
      vol.style.top = `${re.top - rs.top}px`;
      cadre.appendChild(vol);
      flyEl = vol;

      const dx = (rc.left + rc.width / 2) - (re.left + re.width / 2);
      const dy = (rc.top + rc.height / 2) - (re.top + re.height / 2);
      flyAnim = vol.animate(
        [
          { transform: 'translate(0,0) scale(1) rotate(0deg)', opacity: 1 },
          {
            transform: `translate(${dx * 0.45}px, ${dy * 0.45 - 34}px) scale(.6) rotate(5deg)`,
            opacity: 0.95,
            offset: 0.5,
          },
          { transform: `translate(${dx}px, ${dy}px) scale(.17) rotate(9deg)`, opacity: 0 },
        ],
        { duration: FLY_MS, easing: FLY_EASING, fill: 'forwards' },
      );

      const touch = () => {
        window.clearTimeout(safety);
        markArrived();
      };
      flyAnim.onfinish = touch;
      flyAnim.oncancel = touch;
    };

    const reduced = isMotionReduced();
    setState({
      visible: true,
      centerOn: true,
      drawerOpen: reduced,
      drawerAttend: !reduced,
      drawerLabel: reduced ? 'RANGÉ' : 'REÇOIT',
    });

    if (reduced) {
      onUnreadRef.current?.();
      schedule(() => setState((prev) => ({ ...prev, centerOn: false })), REDUCED_CENTER_MS);
      schedule(() => {
        setState((prev) => ({ ...prev, drawerOpen: false }));
        finish();
      }, REDUCED_CLOSE_MS);
      return () => {
        timers.forEach((timer) => window.clearTimeout(timer));
        dropFly();
      };
    }

    schedule(() => {
      setState((prev) => ({
        ...prev,
        drawerOpen: true,
        drawerAttend: true,
        drawerLabel: 'REÇOIT',
      }));
    }, DRAWER_OPEN_DELAY_MS);

    schedule(() => {
      startFly();
      setState((prev) => ({ ...prev, centerOn: false }));
    }, FLY_DELAY_MS);

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      dropFly();
    };
  }, [notice?.id, notice?.name, imageUrl, replayKey, paused, hudCadreRef, tiroirImgRef, centerImgRef]);

  return state;
}

function BagIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path className={styles.bagFill} d="M5 8h14l-1.4 11H6.4Z" />
      <path d="M5 8h14l-1.4 11H6.4Z" />
      <path d="M9 8V6a3 3 0 0 1 6 0v2" />
    </svg>
  );
}

function MusicOnIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M9 18V5l12-2v13" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="18" cy="16" r="3" />
    </svg>
  );
}

function MusicOffIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M9 18V5l12-2v13" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="18" cy="16" r="3" />
      <path d="M3 3l18 18" />
    </svg>
  );
}

function InventoryUnlockDrawer({
  notice,
  imageUrl,
  drawerOpen,
  drawerAttend,
  drawerLabel,
  tiroirImgRef,
}: {
  notice: InventoryUnlockNotice;
  imageUrl?: string;
  drawerOpen: boolean;
  drawerAttend: boolean;
  drawerLabel: 'REÇOIT' | 'RANGÉ';
  tiroirImgRef: RefObject<HTMLImageElement>;
}) {
  return (
    <div
      className={[
        styles.tiroir,
        drawerOpen ? styles.tiroirOuvert : '',
        drawerAttend ? styles.tiroirAttend : '',
      ].filter(Boolean).join(' ')}
      aria-hidden
    >
      {imageUrl ? (
        <img ref={tiroirImgRef} className={styles.tiroirImg} src={imageUrl} alt="" />
      ) : null}
      <div className={styles.tiroirTxt}>
        <i>{drawerLabel}</i>
        <b>{notice.name}</b>
      </div>
    </div>
  );
}

function InventoryUnlockCenter({
  notice,
  imageUrl,
  centerOn,
  centerImgRef,
}: {
  notice: InventoryUnlockNotice;
  imageUrl?: string;
  centerOn: boolean;
  centerImgRef: RefObject<HTMLImageElement>;
}) {
  if (!centerOn) return null;

  return (
    <div className={`${styles.objvol} ${centerOn ? styles.objvolOn : ''}`.trim()} aria-hidden>
      {imageUrl ? (
        <img ref={centerImgRef} className={styles.objvolImg} src={imageUrl} alt="" />
      ) : null}
      <span className={styles.objvolNom}>{notice.name}</span>
    </div>
  );
}

interface PersistentUIProps {
  achievementProgress: { unlocked: number; total: number };
  onMenuClick: () => void;
  onProfileClick: () => void;
  isMenuOpen: boolean;
  isProfileOpen: boolean;
  inventory: string[];
  items: Item[];
  unlockedSkills: string[];
  skills: Skill[];
  achievements: Achievement[];
  unlockedAchievements: string[];
  chapters?: Chapter[];
  missionLabel?: string;
  onNavigateToChapter?: (chapterId: string) => void;
  onBack: () => void;
  onRestart?: () => void;
  profileRef?: React.RefObject<HTMLButtonElement>;
  profileTheme?: ProfileTheme;
  characters?: CharacterPortrait[];
  showShieldHud?: boolean;
  showTopHud?: boolean;
  showBackButton?: boolean;
  showToolIcons?: boolean;
  hudAboveOverlay?: boolean;
  hudProgressLabel?: string | null;
  shieldHudRef?: RefObject<HTMLDivElement>;
  pedaShields?: number;
  pedaShieldsMax?: number;
  shieldHudPulse?: boolean;
  preview?: boolean;
  showInventoryButton?: boolean;
  inventoryDisabled?: boolean;
  onInventoryClick?: () => void;
  inventoryUnlockNotice?: InventoryUnlockNotice | null;
  inventoryUnlockNoticeKey?: number;
  inventoryUnlockNoticeImageUrl?: string;
  inventoryUnlockFxPaused?: boolean;
  inventoryHasUnread?: boolean;
  onInventoryUnlockNoticeEnd?: () => void;
  onInventoryUnreadReceived?: () => void;
  musicEnabled?: boolean;
  onMusicToggle?: () => void;
}

export function PersistentUI({
  achievementProgress,
  onMenuClick,
  onProfileClick,
  isMenuOpen,
  isProfileOpen,
  inventory = [],
  items,
  unlockedSkills,
  skills,
  achievements = [],
  unlockedAchievements = [],
  chapters,
  missionLabel,
  onNavigateToChapter,
  onBack,
  onRestart,
  profileRef,
  profileTheme = 'default',
  characters = [],
  showShieldHud = false,
  showTopHud = true,
  showBackButton = true,
  showToolIcons = true,
  hudAboveOverlay = false,
  hudProgressLabel = null,
  shieldHudRef,
  pedaShields = 0,
  pedaShieldsMax = 0,
  shieldHudPulse = false,
  preview = false,
  showInventoryButton = false,
  inventoryDisabled = false,
  onInventoryClick,
  inventoryUnlockNotice = null,
  inventoryUnlockNoticeKey = 0,
  inventoryUnlockNoticeImageUrl,
  inventoryUnlockFxPaused = false,
  inventoryHasUnread = false,
  onInventoryUnlockNoticeEnd,
  onInventoryUnreadReceived,
  musicEnabled = true,
  onMusicToggle,
}: PersistentUIProps): JSX.Element {
  const { strictMode: a11yStrict, setStrictMode: setA11yStrict } = useAccessibility();
  
  const [activeTab, setActiveTab] = useState<'character' | 'inventory' | 'achievements'>('character');
  const [inventoryRecoit, setInventoryRecoit] = useState(false);
  const hudCadreRef = useRef<HTMLDivElement>(null);
  const tiroirImgRef = useRef<HTMLImageElement>(null);
  const centerImgRef = useRef<HTMLImageElement>(null);
  const inventoryItems = inventory.map(itemId => items.find(item => item.id === itemId)).filter(Boolean) as Item[];
  const showInventoryUnread = inventoryHasUnread || inventoryRecoit;

  const unlockFx = useInventoryUnlockD2(
    inventoryUnlockNotice,
    inventoryUnlockNoticeImageUrl,
    inventoryUnlockNoticeKey,
    inventoryUnlockFxPaused,
    hudCadreRef,
    tiroirImgRef,
    centerImgRef,
    () => {
      setInventoryRecoit(true);
      onInventoryUnreadReceived?.();
    },
    onInventoryUnlockNoticeEnd,
  );

  useEffect(() => {
    if (!inventoryRecoit) return;
    const timer = window.setTimeout(() => setInventoryRecoit(false), DRAWER_RECIBIR_MS);
    return () => window.clearTimeout(timer);
  }, [inventoryRecoit]);

  return (
    <>
      {showInventoryButton && inventoryUnlockNotice && unlockFx.visible && (
        <div className={styles.inventoryUnlockCenterFrame}>
          <div
            ref={hudCadreRef}
            className={`${styles.hudCadre} ${preview ? styles.hudCadrePreview : ''}`}
          >
            <InventoryUnlockCenter
              key={inventoryUnlockNoticeKey}
              notice={inventoryUnlockNotice}
              imageUrl={inventoryUnlockNoticeImageUrl}
              centerOn={unlockFx.centerOn}
              centerImgRef={centerImgRef}
            />
          </div>
        </div>
      )}
      {showTopHud && (
      <div className={`${styles.hudFrame} ${hudAboveOverlay ? styles.hudFrameAboveOverlay : ''}`.trim()}>
        <div className={`${styles.hudCadre} ${preview ? styles.hudCadrePreview : ''}`}>
          <div className={`${styles.outils} cn-zoom-ui`} role="navigation" aria-label="Navigation du jeu">
            {showBackButton && (
              <button
                type="button"
                className={styles.retour}
                onClick={onBack}
                aria-label="Retour à la liste des histoires"
              >
                ◂ retour
              </button>
            )}
            <div className={styles.outilsSpacer} />
            <div className={styles.cluster}>
              {hudProgressLabel && (
                <span className={styles.hudProgress} aria-live="polite">
                  {hudProgressLabel}
                </span>
              )}
              {showInventoryButton && inventoryUnlockNotice && unlockFx.visible && (
                <InventoryUnlockDrawer
                  key={inventoryUnlockNoticeKey}
                  notice={inventoryUnlockNotice}
                  imageUrl={inventoryUnlockNoticeImageUrl}
                  drawerOpen={unlockFx.drawerOpen}
                  drawerAttend={unlockFx.drawerAttend}
                  drawerLabel={unlockFx.drawerLabel}
                  tiroirImgRef={tiroirImgRef}
                />
              )}
              {showToolIcons && showInventoryButton && (
                <div className={styles.inventoryBtnWrap}>
                  <button
                    type="button"
                    className={`${styles.iconBtn} ${styles.inventoryBtn} ${showInventoryUnread ? styles.inventoryBtnUnread : ''} ${inventoryRecoit ? styles.inventoryBtnRecoit : ''}`.trim()}
                    aria-label="Inventaire"
                    disabled={inventoryDisabled}
                    onClick={onInventoryClick}
                  >
                    <BagIcon />
                    {showInventoryUnread ? <span className={styles.inventoryPastille} aria-hidden /> : null}
                  </button>
                </div>
              )}
              {showToolIcons && (
                <button
                  type="button"
                  className={`${styles.iconBtn} ${!musicEnabled ? styles.musicBtnMuted : ''}`.trim()}
                  aria-label={musicEnabled ? 'Couper la musique' : 'Activer la musique'}
                  aria-pressed={!musicEnabled}
                  onClick={onMusicToggle}
                >
                  {musicEnabled ? <MusicOnIcon /> : <MusicOffIcon />}
                </button>
              )}
              {showShieldHud && (
                <>
                  {showToolIcons && (
                    <span className={styles.clusterDivider} aria-hidden />
                  )}
                  <ShieldHud
                    ref={shieldHudRef}
                    total={pedaShields}
                    max={pedaShieldsMax}
                    pulse={shieldHudPulse}
                    compact
                    showProgressBar={false}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Menu Modal */}
      {isMenuOpen && (
        <div 
          className={styles.overlay} 
          onClick={onMenuClick}
          role="dialog"
          aria-modal="true"
          aria-labelledby="menu-title"
        >
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 id="menu-title" className={styles.modalTitle}>Menu</h2>
              <button 
                className={styles.closeBtn} 
                onClick={onMenuClick}
                aria-label="Fermer le menu"
              >✕</button>
            </div>
            <div className={styles.modalContent}>
              <button className={styles.modalButton} onClick={onMenuClick}>
                Reprendre
              </button>
              <button
                type="button"
                className={`${styles.modalButton} ${styles.a11yToggle}`}
                aria-pressed={a11yStrict}
                onClick={() => setA11yStrict(!a11yStrict)}
              >
                Mode accessibilité renforcée : {a11yStrict ? 'activé' : 'désactivé'}
              </button>
              
              {/* Chapter Navigation Section */}
              {chapters && chapters.length > 0 && onNavigateToChapter && (
                <div className={styles.chaptersSection}>
                  {missionLabel && (
                    <p className={styles.missionLabel}>{missionLabel}</p>
                  )}
                  <h3 className={styles.sectionTitle}>Chapitres</h3>
                  <div className={styles.chapterButtons}>
                    {chapters.map((chapter) => (
                      <button 
                        key={chapter.id}
                        className={styles.chapterButton}
                        onClick={() => {
                          onMenuClick();
                          onNavigateToChapter(chapter.id);
                        }}
                      >
                        {chapter.title}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {onRestart && (
                <button className={styles.modalButton} onClick={() => {
                  if (window.confirm('Êtes-vous sûr de vouloir recommencer l\'aventure depuis le début ?')) {
                    onMenuClick();
                    onRestart();
                  }
                }}>
                  Recommencer l'aventure
                </button>
              )}
              <button className={styles.modalButton} onClick={() => {
                onMenuClick();
                onBack();
              }}>
                Retour à la liste des histoires
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Profile Modal — themed per scenario */}
      {isProfileOpen && profileTheme === 'cyberpunk' && (
        <ProfilePanelCyberpunk
          onClose={onProfileClick}
          inventory={inventory}
          items={items}
          unlockedSkills={unlockedSkills}
          skills={skills}
          achievements={achievements}
          unlockedAchievements={unlockedAchievements}
          characters={characters}
        />
      )}

      {/* Default Profile Modal */}
      {isProfileOpen && profileTheme !== 'cyberpunk' && (
        <div 
          className={styles.overlay} 
          onClick={onProfileClick}
          role="dialog"
          aria-modal="true"
          aria-labelledby="profile-title"
        >
          <div className={styles.profilePanel} onClick={(e) => e.stopPropagation()}>
            <div className={styles.profileHeader}>
              <h2 id="profile-title" className={styles.profileTitle}>Profil</h2>
              <button 
                className={styles.closeBtn} 
                onClick={onProfileClick}
                aria-label="Close profile"
              >✕</button>
            </div>

            {/* Tabs */}
            <div className={styles.tabs}>
              <button
                className={`${styles.tab} ${activeTab === 'character' ? styles.tabActive : ''}`}
                onClick={() => setActiveTab('character')}
                aria-selected={activeTab === 'character'}
              >
                👤 Character
              </button>
              <button
                className={`${styles.tab} ${activeTab === 'inventory' ? styles.tabActive : ''}`}
                onClick={() => setActiveTab('inventory')}
                aria-selected={activeTab === 'inventory'}
              >
                🎒 Inventory ({inventory.length})
              </button>
              <button
                className={`${styles.tab} ${activeTab === 'achievements' ? styles.tabActive : ''}`}
                onClick={() => setActiveTab('achievements')}
                aria-selected={activeTab === 'achievements'}
              >
                ⭐ Achievements ({achievementProgress.unlocked}/{achievementProgress.total})
              </button>
            </div>

            {/* Tab Content */}
            <div className={styles.profileContent}>
              {/* Character Tab */}
              {activeTab === 'character' && (
                <div className={styles.tabContent}>
                  <div className={styles.progressSection}>
                    <div className={styles.progressLabel}>
                      Skills: {unlockedSkills.length}/{skills.length}
                    </div>
                    <div className={styles.progressBarContainer}>
                      <div 
                        className={styles.progressBarFill} 
                        style={{ width: `${skills.length > 0 ? (unlockedSkills.length / skills.length) * 100 : 0}%` }}
                      />
                    </div>
                  </div>
                  {skills.length === 0 ? (
                    <div className={styles.emptyState}>
                      <p>No skills available</p>
                    </div>
                  ) : (
                    <div className={styles.skillGrid} role="list" aria-label="Character skills">
                      {skills.map((skill) => {
                        const isUnlocked = unlockedSkills.includes(skill.id);
                        return (
                          <div key={skill.id} role="listitem">
                            <SkillCard skill={skill} isLocked={!isUnlocked} />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Inventory Tab */}
              {activeTab === 'inventory' && (
                <div className={styles.tabContent}>
                  {inventoryItems.length === 0 ? (
                    <div className={styles.emptyState}>
                      <p>Your inventory is empty</p>
                    </div>
                  ) : (
                    <div className={styles.itemGrid} role="list" aria-label="Inventory items">
                      {inventoryItems.map((item) => (
                        <div key={item.id} role="listitem">
                          <ItemCard item={item} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Achievements Tab */}
              {activeTab === 'achievements' && (
                <div className={styles.tabContent}>
                  <div className={styles.progressSection}>
                    <div className={styles.progressLabel}>
                      Achievements: {achievementProgress.unlocked}/{achievementProgress.total}
                    </div>
                    <div className={styles.progressBarContainer}>
                      <div 
                        className={styles.progressBarFill} 
                        style={{ width: `${achievementProgress.total > 0 ? (achievementProgress.unlocked / achievementProgress.total) * 100 : 0}%` }}
                      />
                    </div>
                  </div>
                  {achievements.length === 0 ? (
                    <div className={styles.emptyState}>
                      <p>No achievements available</p>
                    </div>
                  ) : (
                    <div className={styles.achievementGrid} role="list" aria-label="Achievements">
                      {achievements.map((achievement) => {
                        const isUnlocked = unlockedAchievements.includes(achievement.id);
                        return (
                          <div 
                            key={achievement.id} 
                            role="listitem"
                            className={`${styles.achievementCard} ${isUnlocked ? styles.achievementUnlocked : styles.achievementLocked}`}
                          >
                            {achievement.icon && (
                              <div className={styles.achievementIcon}>
                                <img src={achievement.icon} alt="" />
                              </div>
                            )}
                            <div className={styles.achievementInfo}>
                              <h3 className={styles.achievementName}>{achievement.name}</h3>
                              <p className={styles.achievementDescription}>{achievement.description}</p>
                            </div>
                            {isUnlocked && (
                              <div className={styles.achievementBadge}>✓</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

